<template>
    <div class="container-fluid py-4">
      <div class="row">
        <div v-if="loading" class="text-center">
          <p>Cargando...</p>
          <div class="spinner-border" role="status">
            <span class="visually-hidden">Cargando...</span>
          </div>
        </div>
  
        <div v-else class="card">
          <div class="p-3 card-header">
            <div class="d-flex justify-content-between">
              <h6 class="mb-2"><i class="fas fa-tags pe-2"></i>Categorías</h6>
              <button class="btn btn-dark btn-sm mb-0" @click="openCreateModal">
                + Nueva Categoría
              </button>
            </div>
          </div>
  
          <div class="table-responsive pb-4">
            <table class="table align-items-center mb-0">
              <thead>
                <tr>
                  <th class="text-uppercase text-dark text-xxs font-weight-bolder opacity-7">Nombre</th>
                  <th class="text-uppercase text-dark text-xxs font-weight-bolder opacity-7 ps-2">Descripción</th>
                  <th class="text-uppercase text-dark text-xxs font-weight-bolder opacity-7 ps-2">Acciones</th>

                </tr>
              </thead>
              <tbody>
                <tr v-for="category in categories" :key="category.id" class="row-hover">
                  <td class="px-2 py-1">
                    <p class="text-xs font-weight-bold mb-0">
                      {{ category.nombre }}
                    </p>
                  </td>
                  <td class="px-2 py-1">
                    <p class="text-xs font-weight-bold mb-0">
                      {{ category.descripcion }}
                    </p>
                  </td>
                  <td class="align-middle">
                    <div class="dropdown-xs w-40">
                      <button class="btn btn-link text-lg text-primary dropdown-toggle p-0 mb-0" type="button"
                        id="dropdownMenuButton" data-bs-toggle="dropdown" aria-expanded="false">
                        ...
                      </button>
                      <ul class="dropdown-menu w-20" aria-labelledby="dropdownMenuButton">
                        <li>
                          <a class="dropdown-item edit-action" @click="openEditModal(category)">
                            <i class="fas fa-edit pe-2"></i>Editar
                          </a>
                        </li>
                        <li>
                          <a class="dropdown-item delete-action text-danger" @click="confirmDelete(category.id)">
                            <i class="fas fa-trash pe-2"></i>Eliminar
                          </a>
                        </li>
                      </ul>
                    </div>
                  </td>
                </tr>
                <tr v-if="categories.length === 0">
                  <td colspan="3" class="text-center py-4">
                    No hay categorías registradas
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>
  
      <!-- Create/Edit Modal -->
      <div class="modal fade" id="categoryModal" tabindex="-1" role="dialog" aria-hidden="true">
        <div class="modal-dialog modal-dialog-centered" role="document">
          <div class="modal-content">
            <div class="modal-header">
              <h5 class="modal-title">{{ isEditing ? 'Editar Categoría' : 'Nueva Categoría' }}</h5>
              <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
            </div>
            <div class="modal-body">
              <form @submit.prevent="submitForm">
                <div class="mb-3">
                  <label for="nombre" class="form-label">Nombre</label>
                  <input 
                    type="text" 
                    class="form-control" 
                    id="nombre" 
                    v-model="form.nombre"
                    required
                  >
                </div>
                <div class="mb-3">
                  <label for="descripcion" class="form-label">Descripción</label>
                  <textarea 
                    class="form-control" 
                    id="descripcion" 
                    rows="3"
                    v-model="form.descripcion"
                  ></textarea>
                </div>
                <div class="modal-footer">
                  <button type="button" class="btn btn-secondary mb-0" data-bs-dismiss="modal">Cancelar</button>
                  <button type="submit" class="btn btn-primary mb-0">
                    <span v-if="loading">
                      <i class="fas fa-spinner fa-spin me-1"></i>Procesando...
                    </span>
                    <span v-else>
                      {{ isEditing ? 'Actualizar' : 'Guardar' }}
                    </span>
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  </template>
  
  <script setup>
import { ref, onMounted } from 'vue'
import { Modal } from 'bootstrap'
import Swal from 'sweetalert2'
import { CategoriesClient } from '@/services/CategoriesClient'

const client = new CategoriesClient()

  
  // Reactive state
  const categories = ref([])
  const form = ref({
    id: null,
    nombre: '',
    descripcion: ''
  })
  const isEditing = ref(false)
  const loading = ref(false)
  const modal = ref(null)
  
const fetchCategories = async () => {
  loading.value = true
  try {
    const response = await client.getAll()
    
    categories.value = response
  } catch (error) {
    console.error('Error fetching categories:', error)
    Swal.fire('Error', 'No se pudieron cargar las categorías', 'error')
  } finally {
    loading.value = false
  }
}

const submitForm = async () => {
  loading.value = true
  try {
    const dto = {
      nombre: form.value.nombre,
      descripcion: form.value.descripcion
    }
   

    if (isEditing.value) {
      await client.updateCategory(form.value.id, dto)
      Swal.fire('Éxito', 'Categoría actualizada correctamente', 'success')
    } else {
      await client.createCategory(dto)
      Swal.fire('Éxito', 'Categoría creada correctamente', 'success')
    }

    modal.value.hide()
    await fetchCategories()
  } catch (error) {
    console.error('Error saving category:', error)
    Swal.fire('Error', 'Ocurrió un error al guardar la categoría', 'error')
  } finally {
    loading.value = false
  }
}

const confirmDelete = (id) => {
  Swal.fire({
    title: '¿Estás seguro?',
    text: "No podrás revertir esta acción",
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Sí, eliminar',
    cancelButtonText: 'Cancelar'
  }).then(async (result) => {
    if (result.isConfirmed) {
      try {
        await client.deleteCategory(id)
        .catch(e => {
            if (!e.message.includes('JSON')) throw e; // Ignore JSON parse errors
        });
        Swal.fire('Eliminado', 'La categoría ha sido eliminada', 'success')
        await fetchCategories()
      } catch (error) {
        console.error('Error deleting category:', error)
        Swal.fire('Error', 'No se pudo eliminar la categoría', 'error')
      }
    }
  })
}

  
  const openCreateModal = () => {
    resetForm()
    isEditing.value = false
    modal.value.show()
  }
  
  const openEditModal = (category) => {
    form.value = {
      id: category.id,
      nombre: category.nombre,
      descripcion: category.descripcion
    }
    isEditing.value = true
    modal.value.show()
  }
  
  const resetForm = () => {
    form.value = {
      id: null,
      nombre: '',
      descripcion: ''
    }
  }
  
  // Lifecycle hooks
  onMounted(() => {
    modal.value = new Modal(document.getElementById('categoryModal'))
    fetchCategories()
  })
  </script>
  
  <style scoped>

  .table-responsive {
    max-height: 500px;
    overflow-y: auto;
  }
  
  .modal-content {
    border: none;
    border-radius: 12px;
  }
  
  .modal-header {
    border-bottom: 1px solid #e9ecef;
    background-color: #f8f9fa;
    border-radius: 12px 12px 0 0;
  }
  
  .btn-close {
    background-size: 0.8em;
  }
  
  .form-control:focus {
    border-color: #4a4a4a;
    box-shadow: 0 0 0 0.25rem rgba(74, 74, 74, 0.25);
  }
  
  .row-hover:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
  
  .dropdown-xs .dropdown-menu {
    min-width: 10rem;
    font-size: 1rem;
  }
  </style>